export interface RoyaltyProcessOwnershipSlice {
  series_id: number
  studio_internal_id: number | null
  start_date: string | null
  end_date: string | null
  ownership_phase: string | null
}

export interface RoyaltyProcessOwnershipSeries {
  series_id: number
  slices: RoyaltyProcessOwnershipSlice[]
}

function dateKey(value: string | null): string | null {
  return value ? String(value).slice(0, 10) : null
}

export function groupRoyaltyProcessOwnershipsBySeries(
  ownerships: RoyaltyProcessOwnershipSlice[]
): RoyaltyProcessOwnershipSeries[] {
  const bySeries = new Map<number, RoyaltyProcessOwnershipSlice[]>()

  for (const ownership of ownerships) {
    const list = bySeries.get(ownership.series_id) ?? []
    list.push(ownership)
    bySeries.set(ownership.series_id, list)
  }

  return [...bySeries.entries()]
    .sort(([leftSeriesId], [rightSeriesId]) => leftSeriesId - rightSeriesId)
    .map(([seriesId, slices]) => ({
      series_id: seriesId,
      slices: [...slices].sort((left, right) => {
        const byStart = (dateKey(left.start_date) ?? '').localeCompare(dateKey(right.start_date) ?? '')
        if (byStart !== 0) return byStart
        const byEnd = (dateKey(left.end_date) ?? '9999-12-31').localeCompare(
          dateKey(right.end_date) ?? '9999-12-31'
        )
        if (byEnd !== 0) return byEnd
        return (left.studio_internal_id ?? 0) - (right.studio_internal_id ?? 0)
      }),
    }))
}

export function findRoyaltyProcessOwnershipForDate(
  slices: RoyaltyProcessOwnershipSlice[],
  metricDate: string
): RoyaltyProcessOwnershipSlice | null {
  let selected: RoyaltyProcessOwnershipSlice | null = null

  for (const slice of slices) {
    const start = dateKey(slice.start_date) ?? '-infinity'
    const end = dateKey(slice.end_date) ?? '9999-12-31'
    if (start > metricDate || end < metricDate) continue

    // Mirrors the engine's preference for the latest ownership start when bad data
    // creates overlapping ownership windows for the same series and day.
    const selectedStart = dateKey(selected?.start_date ?? null) ?? '-infinity'
    if (!selected || start > selectedStart) selected = slice
  }

  return selected
}

export function isZeroRevenueBillableOwnershipPhase(phase: string | null | undefined): boolean {
  return phase === 'BUYING' || phase === 'PLANNED'
}

export function areConsecutiveRoyaltyProcessDates(previousDate: string, nextDate: string): boolean {
  const previous = new Date(`${previousDate}T00:00:00.000Z`)
  if (Number.isNaN(previous.getTime())) return false
  previous.setUTCDate(previous.getUTCDate() + 1)
  return previous.toISOString().slice(0, 10) === nextDate
}

export function canExtendRoyaltyProcessSegment(
  previous: { identity: string; end: string } | null,
  next: { identity: string; date: string }
): boolean {
  return previous != null
    && previous.identity === next.identity
    && areConsecutiveRoyaltyProcessDates(previous.end, next.date)
}
